import { useEffect, useRef, useState, type ReactNode } from "react";

/**
 * Renders children only once the placeholder gets close to the viewport.
 * Purely a performance wrapper: the reserved box keeps layout identical (no
 * CLS) and the content looks exactly the same once mounted.
 */
export function LazyMount({
  children,
  minHeight,
  rootMargin = "600px 0px",
  className,
}: {
  children: ReactNode;
  minHeight: number;
  rootMargin?: string;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (show) return;
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setShow(true);
      return;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setShow(true);
          obs.disconnect();
        }
      },
      { rootMargin },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [show, rootMargin]);

  return (
    <div ref={ref} className={className} style={show ? undefined : { minHeight }}>
      {show ? children : null}
    </div>
  );
}
